import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/enhanced-button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { ResizeHandle } from "@/components/ui/resize-handle";
import { cn } from "@/lib/utils";
import {
  ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight,
  Search, X, Check, ChevronDown, Download,
} from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useColumnResize } from "@/hooks/use-column-resize";
import { VendorDto, VendorPagination, VendorApiResponse } from "@blinkbooks/types";
import apiClient from "@/utils/httpClient";

// ─── Types ───────────────────────────────────────────────────────────────────

type SortField = "companyName" | "lastPaymentDate" | "ytdSpend" | "openBalance";
type SortDirection = "asc" | "desc" | null;

interface VendorsProps {
  onVendorClick?: (vendorName: string) => void;
}

// ─── Formatters ──────────────────────────────────────────────────────────────

const formatAmount = (n: number | undefined | null): string | null => {
  if (n == null) return null;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return `$${Math.round(n).toLocaleString("en-US")}`;
};

const formatRelativeDate = (isoStr?: string): string | null => {
  if (!isoStr) return null;
  const date = new Date(isoStr);
  if (isNaN(date.getTime())) return null;
  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.floor(diffMs / 86_400_000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 14) return "Last week";
  if (diffDays < 61) return `${Math.floor(diffDays / 7)}w ago`;
  const thisYear = new Date().getFullYear() === date.getFullYear();
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(thisYear ? {} : { year: "numeric" }),
  });
};

// ─── Avatar ───────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  "bg-blue-500", "bg-emerald-500", "bg-violet-500", "bg-amber-500",
  "bg-rose-500", "bg-cyan-500", "bg-orange-500", "bg-indigo-500",
  "bg-teal-500", "bg-pink-500",
];
const getAvatarColor = (name: string) => {
  const hash = name.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

// ─── Sparkline ────────────────────────────────────────────────────────────────

const TrendCell = ({ data }: { data?: number[] }) => {
  const hasData = data && data.some(v => v > 0);

  if (!hasData) {
    return (
      <svg width={72} height={24} viewBox="0 0 72 24" aria-hidden>
        <line
          x1={6} y1={12} x2={66} y2={12}
          strokeWidth={1.5}
          strokeDasharray="3 3"
          strokeLinecap="round"
          className="stroke-muted-foreground/20"
        />
      </svg>
    );
  }

  const W = 72, H = 24, pad = 2;
  const min = Math.min(...data!);
  const max = Math.max(...data!);
  const range = max - min || 1;
  const pts = data!
    .map((v, i) => `${(i / (data!.length - 1)) * W},${H - ((v - min) / range) * (H - pad * 2) - pad}`)
    .join(" ");
  const rising = data![data!.length - 1] >= data![0];

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="overflow-visible" aria-hidden>
      <polyline
        points={pts}
        fill="none"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={rising ? "stroke-emerald-500" : "stroke-rose-500"}
      />
    </svg>
  );
};

// ─── Skeleton row ─────────────────────────────────────────────────────────────

const SkeletonRow = ({ gridTemplate }: { gridTemplate: string }) => (
  <div
    className="grid gap-3 px-4 py-3.5 border-b last:border-b-0 items-center"
    style={{ gridTemplateColumns: gridTemplate }}
  >
    <div className="flex items-center gap-3">
      <Skeleton className="w-9 h-9 rounded-lg shrink-0" />
      <div className="flex flex-col gap-2 flex-1 min-w-0">
        <Skeleton className="h-3.5 w-32 rounded" />
        <Skeleton className="h-3 w-20 rounded" />
      </div>
    </div>
    <Skeleton className="h-3.5 w-16 rounded ml-auto" />
    <Skeleton className="h-4 w-16 rounded" />
    <Skeleton className="h-5 w-14 rounded-full" />
    <Skeleton className="h-3.5 w-16 rounded" />
    <div />
  </div>
);

// ─── Filter constants ─────────────────────────────────────────────────────────

const SPEND_PRESETS = [
  { label: "Any", min: undefined, max: undefined },
  { label: "< $1k",    min: undefined, max: 1_000 },
  { label: "$1k–$10k", min: 1_000,     max: 10_000 },
  { label: "$10k–$50k",min: 10_000,    max: 50_000 },
  { label: "> $50k",   min: 50_000,    max: undefined },
] as const;

// 6 columns: Vendor | YTD Spend | 12-Wk | Open Balance | Last Paid | →
const COL_WIDTHS = [280, 130, 100, 140, 130, 44];

// ─── Main component ───────────────────────────────────────────────────────────

const Vendors = ({ onVendorClick }: VendorsProps) => {
  const [vendors, setVendors]       = useState<VendorDto[]>([]);
  const [isLoading, setIsLoading]   = useState(true);
  const [sortField, setSortField]   = useState<SortField>("companyName");
  const [sortDir, setSortDir]       = useState<SortDirection>("asc");

  // Applied filters
  const [company,       setCompany]       = useState("");
  const [mode,          setMode]          = useState<"includes" | "excludes">("includes");
  const [inactive,      setInactive]      = useState(false);
  const [category,      setCategory]      = useState("all");
  const [lastPaid,      setLastPaid]      = useState<"all" | "30" | "60" | "90">("all");
  const [spendIdx,      setSpendIdx]      = useState(0);
  const [openBalFilter, setOpenBalFilter] = useState(false);

  // Pending (staged) filters
  const [pCompany,   setPCompany]   = useState("");
  const [pMode,      setPMode]      = useState<"includes" | "excludes">("includes");
  const [pInactive,  setPInactive]  = useState(false);
  const [pCategory,  setPCategory]  = useState("all");
  const [pLastPaid,  setPLastPaid]  = useState<"all" | "30" | "60" | "90">("all");
  const [pSpend,     setPSpend]     = useState(0);
  const [pOpenBal,   setPOpenBal]   = useState(false);

  const [categories,  setCategories]  = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination,  setPagination]  = useState<VendorPagination>({ page: 1, limit: 50, total: 0, totalPages: 0 });

  const isMobile = useIsMobile();
  const { gridTemplate, startResize } = useColumnResize(COL_WIDTHS);

  const pendingDirty =
    pCompany !== company || pMode !== mode || pInactive !== inactive ||
    pCategory !== category || pLastPaid !== lastPaid ||
    pSpend !== spendIdx || pOpenBal !== openBalFilter;

  const hasActiveFilters =
    company !== "" || inactive || category !== "all" ||
    lastPaid !== "all" || spendIdx !== 0 || openBalFilter;

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchVendors = useCallback(async (
    page     = 1,
    co       = company,
    m: "includes" | "excludes" = mode,
    inc      = inactive,
    cat      = category,
    lp: "all" | "30" | "60" | "90" = lastPaid,
    si       = spendIdx,
    ob       = openBalFilter,
  ) => {
    setIsLoading(true);
    try {
      const p = new URLSearchParams();
      p.set("page", String(page));
      p.set("limit", "50");
      if (co)       { p.set("companyName", co); p.set("companyNameMode", m); }
      if (!inc)     p.set("isActive", "true");
      if (cat !== "all") p.set("category", cat);
      if (lp !== "all") {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - parseInt(lp));
        p.set("lastPaidAfter", cutoff.toISOString().split("T")[0]);
      }
      const preset = SPEND_PRESETS[si];
      if (preset?.min !== undefined) p.set("minSpend", String(preset.min));
      if (preset?.max !== undefined) p.set("maxSpend", String(preset.max));
      if (ob) p.set("hasOpenBalance", "true");

      const res = await apiClient.getRaw<VendorApiResponse>(`/vendors?${p.toString()}`);
      if (res?.success) {
        setVendors(res.data ?? []);
        const pg = res.pagination ?? { page: 1, limit: 50, total: (res.data ?? []).length, totalPages: 1 };
        setPagination(pg);
        setCurrentPage(pg.page);
      } else {
        setVendors([]);
        setPagination({ page: 1, limit: 50, total: 0, totalPages: 0 });
      }
    } catch {
      setVendors([]);
      setPagination({ page: 1, limit: 50, total: 0, totalPages: 0 });
    } finally {
      setIsLoading(false);
    }
  }, [company, mode, inactive, category, lastPaid, spendIdx, openBalFilter]);

  useEffect(() => {
    fetchVendors(1, "", "includes", false, "all", "all", 0, false);
    apiClient.get<string[]>("/vendors/categories")
      .then(d => { if (d) setCategories(d); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => {
    setCompany(pCompany); setMode(pMode); setInactive(pInactive);
    setCategory(pCategory); setLastPaid(pLastPaid); setSpendIdx(pSpend); setOpenBalFilter(pOpenBal);
    setCurrentPage(1);
    fetchVendors(1, pCompany, pMode, pInactive, pCategory, pLastPaid, pSpend, pOpenBal);
  };

  const resetFilters = () => {
    setPCompany(""); setPMode("includes"); setPInactive(false);
    setPCategory("all"); setPLastPaid("all"); setPSpend(0); setPOpenBal(false);
    setCompany(""); setMode("includes"); setInactive(false);
    setCategory("all"); setLastPaid("all"); setSpendIdx(0); setOpenBalFilter(false);
    setCurrentPage(1);
    fetchVendors(1, "", "includes", false, "all", "all", 0, false);
  };

  const goToPage = (page: number) => {
    if (page < 1 || page > pagination.totalPages) return;
    setCurrentPage(page);
    fetchVendors(page, company, mode, inactive, category, lastPaid, spendIdx, openBalFilter);
  };

  // ── Sort ───────────────────────────────────────────────────────────────────

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDir === "asc")  setSortDir("desc");
      else if (sortDir === "desc") { setSortDir(null); setSortField("companyName"); }
      else setSortDir("asc");
    } else { setSortField(field); setSortDir("asc"); }
  };

  const sorted = [...vendors].sort((a, b) => {
    if (!sortDir) return 0;
    let av: string | number = 0, bv: string | number = 0;
    if (sortField === "companyName") {
      av = a.companyName?.toLowerCase() ?? "";
      bv = b.companyName?.toLowerCase() ?? "";
    } else if (sortField === "ytdSpend") {
      av = a.ytdSpend ?? -1; bv = b.ytdSpend ?? -1;
    } else if (sortField === "openBalance") {
      av = a.openBalance ?? 0; bv = b.openBalance ?? 0;
    } else if (sortField === "lastPaymentDate") {
      av = a.lastPaymentDate ? new Date(a.lastPaymentDate).getTime() : 0;
      bv = b.lastPaymentDate ? new Date(b.lastPaymentDate).getTime() : 0;
    }
    return av < bv ? (sortDir === "asc" ? -1 : 1) : av > bv ? (sortDir === "asc" ? 1 : -1) : 0;
  });

  // ── Sub-components ─────────────────────────────────────────────────────────

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-30" />;
    if (sortDir === "asc")   return <ArrowUp   className="h-3 w-3 text-foreground/70" />;
    if (sortDir === "desc")  return <ArrowDown  className="h-3 w-3 text-foreground/70" />;
    return <ArrowUpDown className="h-3 w-3 opacity-30" />;
  };

  const ColHeader = ({ field, children, align = "left" }: { field: SortField; children: React.ReactNode; align?: "left" | "right" }) => (
    <button
      onClick={() => toggleSort(field)}
      className={cn(
        "flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground/80 transition-colors select-none",
        align === "right" && "ml-auto",
      )}
    >
      {children}
      <SortIcon field={field} />
    </button>
  );

  const FilterPill = ({
    label, active, children,
  }: { label: string; active: boolean; children: React.ReactNode }) => (
    <Popover>
      <PopoverTrigger asChild>
        <button className={cn(
          "flex items-center gap-1.5 h-8 px-3 rounded-md border text-sm whitespace-nowrap transition-all",
          active
            ? "border-foreground/20 bg-muted/50 text-foreground font-medium"
            : "border-input bg-background text-muted-foreground hover:border-foreground/20 hover:text-foreground",
        )}>
          {label}
          <ChevronDown className="h-3 w-3 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-1 w-auto min-w-[140px]" align="start">
        {children}
      </PopoverContent>
    </Popover>
  );

  const FilterOption = ({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: React.ReactNode }) => (
    <button onClick={onClick} className="w-full flex items-center gap-2 px-2.5 py-1.5 text-sm rounded hover:bg-muted/80 text-left transition-colors">
      <Check className={cn("h-3.5 w-3.5 shrink-0 text-foreground", selected ? "opacity-100" : "opacity-0")} />
      {children}
    </button>
  );

  // ── Empty states ───────────────────────────────────────────────────────────

  const EmptyState = () => {
    if (hasActiveFilters) {
      return (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <div className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center">
            <Search className="h-4 w-4 text-muted-foreground/60" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">No vendors match these filters</p>
            <p className="text-xs text-muted-foreground mt-1">Try adjusting or clearing your filters</p>
          </div>
          <button onClick={resetFilters} className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors">
            Clear all filters
          </button>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <div className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center">
          <Download className="h-4 w-4 text-muted-foreground/60" />
        </div>
        <div>
          <p className="text-sm font-medium text-foreground">No vendors synced yet</p>
          <p className="text-xs text-muted-foreground mt-1">Connect QuickBooks to import your vendor directory</p>
        </div>
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Card className="m-6 shadow-sm border-border/60">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="px-6 pt-6 pb-5 border-b border-border/60">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <p className="text-[11px] font-semibold tracking-widest uppercase text-muted-foreground/60 mb-1">
              Who You Pay
            </p>
            <h1 className="text-[22px] font-bold tracking-tight text-foreground leading-none">
              Vendors
            </h1>
          </div>
          <div className="flex items-center gap-2 pt-1">
            {pagination.total > 0 && !isLoading && (
              <span className="text-sm text-muted-foreground tabular-nums">
                {pagination.total.toLocaleString()} vendor{pagination.total !== 1 ? "s" : ""}
              </span>
            )}
            <Button variant="outline" size="sm" className="h-8 gap-1.5 text-sm font-normal text-muted-foreground">
              <Download className="h-3.5 w-3.5" />
              Export
            </Button>
          </div>
        </div>

        {/* ── Filter bar ───────────────────────────────────────────────────── */}
        <div className="flex items-center gap-2 flex-wrap">

          {/* Search + Inc/Exc */}
          <div className="flex items-center h-8 rounded-md border border-input bg-background overflow-hidden">
            <button
              onClick={() => setPMode(m => m === "includes" ? "excludes" : "includes")}
              title={`Toggle to ${pMode === "includes" ? "exclude" : "include"}`}
              className={cn(
                "h-full px-2.5 text-xs font-semibold border-r border-input transition-colors whitespace-nowrap",
                pMode === "excludes"
                  ? "bg-destructive/10 text-destructive"
                  : "text-muted-foreground hover:bg-muted/50",
              )}
            >
              {pMode === "includes" ? "Inc." : "Exc."}
            </button>
            <div className="relative flex items-center">
              <Search className="absolute left-2.5 h-3.5 w-3.5 text-muted-foreground/50 pointer-events-none" />
              <input
                type="text"
                placeholder="Search vendors…"
                value={pCompany}
                onChange={e => setPCompany(e.target.value)}
                onKeyDown={e => e.key === "Enter" && applyFilters()}
                className="pl-8 pr-8 bg-transparent outline-none text-sm h-8 w-44"
              />
              {pCompany && (
                <button
                  onClick={() => setPCompany("")}
                  className="absolute right-2 text-muted-foreground/40 hover:text-muted-foreground transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>

          {/* Status */}
          <FilterPill label={`Status: ${pInactive ? "All" : "Active"}`} active={pInactive}>
            <FilterOption selected={!pInactive} onClick={() => setPInactive(false)}>Active only</FilterOption>
            <FilterOption selected={pInactive}  onClick={() => setPInactive(true)}>All vendors</FilterOption>
          </FilterPill>

          {/* Category */}
          <FilterPill
            label={`Category: ${pCategory === "all" ? "All" : pCategory}`}
            active={pCategory !== "all"}
          >
            <div className="max-h-56 overflow-y-auto">
              <FilterOption selected={pCategory === "all"} onClick={() => setPCategory("all")}>All</FilterOption>
              {categories.map(c => (
                <FilterOption key={c} selected={pCategory === c} onClick={() => setPCategory(c)}>
                  <span className="truncate max-w-[180px]">{c}</span>
                </FilterOption>
              ))}
              {categories.length === 0 && (
                <p className="px-2.5 py-1.5 text-xs text-muted-foreground">No categories loaded</p>
              )}
            </div>
          </FilterPill>

          {/* Last paid */}
          <FilterPill
            label={`Last paid: ${pLastPaid === "all" ? "Any" : `≤ ${pLastPaid}d`}`}
            active={pLastPaid !== "all"}
          >
            {(["all", "30", "60", "90"] as const).map(d => (
              <FilterOption key={d} selected={pLastPaid === d} onClick={() => setPLastPaid(d)}>
                {d === "all" ? "Any time" : `Last ${d} days`}
              </FilterOption>
            ))}
          </FilterPill>

          {/* Spend range */}
          <FilterPill
            label={`Spend: ${SPEND_PRESETS[pSpend]?.label ?? "Any"}`}
            active={pSpend !== 0}
          >
            {SPEND_PRESETS.map((p, i) => (
              <FilterOption key={i} selected={pSpend === i} onClick={() => setPSpend(i)}>
                {p.label}
              </FilterOption>
            ))}
          </FilterPill>

          {/* Open balance toggle */}
          <button
            onClick={() => setPOpenBal(v => !v)}
            className={cn(
              "flex items-center gap-1.5 h-8 px-3 rounded-md border text-sm whitespace-nowrap transition-all",
              pOpenBal
                ? "border-amber-400/50 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 font-medium"
                : "border-input bg-background text-muted-foreground hover:border-foreground/20 hover:text-foreground",
            )}
          >
            <div className={cn("w-1.5 h-1.5 rounded-full shrink-0 transition-colors", pOpenBal ? "bg-amber-500" : "bg-muted-foreground/30")} />
            Open balance
          </button>

          {/* Apply / Reset */}
          <div className="flex items-center gap-1.5 ml-auto">
            <Button
              variant="default"
              size="sm"
              onClick={applyFilters}
              disabled={!pendingDirty || isLoading}
              className="h-8 px-4 text-sm"
            >
              Apply
            </Button>
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="h-8 px-2 flex items-center text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                title="Clear all filters"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      <CardContent className="p-0">
        {isMobile ? (
          /* ── Mobile list ─────────────────────────────────────────────────── */
          <div>
            {isLoading ? (
              <div className="divide-y divide-border/50">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3.5">
                    <Skeleton className="w-10 h-10 rounded-lg shrink-0" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-3.5 w-36 rounded" />
                      <Skeleton className="h-3 w-24 rounded" />
                    </div>
                    <Skeleton className="h-4 w-12 rounded" />
                  </div>
                ))}
              </div>
            ) : sorted.length === 0 ? (
              <EmptyState />
            ) : (
              <div className="divide-y divide-border/50">
                {sorted.map(v => (
                  <button
                    key={v.id}
                    onClick={() => onVendorClick?.(v.companyName)}
                    className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-muted/30 transition-colors text-left"
                  >
                    <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center text-white text-sm font-bold shrink-0", getAvatarColor(v.companyName))}>
                      {v.companyName[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <div className={cn("w-1.5 h-1.5 rounded-full shrink-0", v.isActive ? "bg-emerald-500" : "bg-gray-400")} />
                        <span className="text-sm font-medium truncate">{v.companyName}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {[v.primaryCategory, formatRelativeDate(v.lastPaymentDate) ? `Last paid ${formatRelativeDate(v.lastPaymentDate)}` : null]
                          .filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      {v.ytdSpend != null && v.ytdSpend > 0 ? (
                        <span className="text-sm font-semibold tabular-nums">{formatAmount(v.ytdSpend)}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground/40">No spend</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* ── Desktop table ───────────────────────────────────────────────── */
          <div className="overflow-x-auto">
            {/* Sticky header */}
            <div
              className="grid gap-3 px-4 py-2.5 bg-muted/20 border-b border-border/60 sticky top-0 z-10 [&>*]:min-w-0 backdrop-blur-sm"
              style={{ gridTemplateColumns: gridTemplate }}
            >
              <div className="relative group overflow-hidden">
                <ColHeader field="companyName">Vendor</ColHeader>
                <ResizeHandle onMouseDown={e => startResize(0, e)} className="opacity-0 group-hover:opacity-100" />
              </div>
              <div className="relative group overflow-hidden flex justify-end">
                <ColHeader field="ytdSpend" align="right">YTD Spend</ColHeader>
                <ResizeHandle onMouseDown={e => startResize(1, e)} className="opacity-0 group-hover:opacity-100" />
              </div>
              <div className="relative group overflow-hidden">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">12-Wk</span>
                <ResizeHandle onMouseDown={e => startResize(2, e)} className="opacity-0 group-hover:opacity-100" />
              </div>
              <div className="relative group overflow-hidden">
                <ColHeader field="openBalance">Open Balance</ColHeader>
                <ResizeHandle onMouseDown={e => startResize(3, e)} className="opacity-0 group-hover:opacity-100" />
              </div>
              <div className="relative group overflow-hidden">
                <ColHeader field="lastPaymentDate">Last Paid</ColHeader>
                <ResizeHandle onMouseDown={e => startResize(4, e)} className="opacity-0 group-hover:opacity-100" />
              </div>
              <div />
            </div>

            {/* Body */}
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <SkeletonRow key={i} gridTemplate={gridTemplate} />
              ))
            ) : sorted.length === 0 ? (
              <EmptyState />
            ) : (
              sorted.map(vendor => {
                const relDate    = formatRelativeDate(vendor.lastPaymentDate);
                const hasSpend   = vendor.ytdSpend != null && vendor.ytdSpend > 0;
                const hasBalance = vendor.openBalance != null && vendor.openBalance > 0;

                return (
                  <div
                    key={vendor.id}
                    className="group grid gap-3 px-4 py-3.5 border-b border-border/40 last:border-b-0 [&>*]:min-w-0 items-center"
                    style={{ gridTemplateColumns: gridTemplate }}
                  >
                    {/* ── Vendor cell ──────────────────────────────── */}
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={cn(
                        "w-9 h-9 rounded-lg flex items-center justify-center text-white text-sm font-bold shrink-0",
                        getAvatarColor(vendor.companyName),
                      )}>
                        {vendor.companyName[0]?.toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={cn("w-1.5 h-1.5 rounded-full shrink-0", vendor.isActive ? "bg-emerald-500" : "bg-gray-300 dark:bg-gray-600")}
                            title={vendor.isActive ? "Active" : "Inactive"}
                          />
                          <span className="text-[13.5px] font-medium text-foreground truncate leading-none">
                            {vendor.companyName}
                          </span>
                        </div>
                        {vendor.primaryCategory && (
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">
                            {vendor.primaryCategory}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* ── YTD Spend ────────────────────────────────── */}
                    <div className="text-right">
                      {hasSpend ? (
                        <span className="text-[13.5px] font-semibold text-foreground tabular-nums">
                          {formatAmount(vendor.ytdSpend)}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/40 font-normal">
                          No activity
                        </span>
                      )}
                    </div>

                    {/* ── 12-Wk Trend ──────────────────────────────── */}
                    <div className="flex items-center">
                      <TrendCell data={vendor.weeklyTrend} />
                    </div>

                    {/* ── Open Balance ─────────────────────────────── */}
                    <div>
                      {hasBalance ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold tabular-nums bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-200/70 dark:border-amber-800/50">
                          {formatAmount(vendor.openBalance)}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/30 font-normal">—</span>
                      )}
                    </div>

                    {/* ── Last Paid ────────────────────────────────── */}
                    <div>
                      {relDate ? (
                        <span className="text-[13px] text-foreground/80 tabular-nums">{relDate}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground/40">No payments</span>
                      )}
                    </div>

                    {/* ── View action (only clickable element) ─────── */}
                    <div className="flex items-center justify-end">
                      <button
                        onClick={() => onVendorClick?.(vendor.companyName)}
                        className="flex items-center gap-1 px-2 py-1 rounded-md text-muted-foreground/50 hover:text-foreground hover:bg-muted/60 transition-colors"
                        title={`View bills for ${vendor.companyName}`}
                      >
                        <span className="text-[11px] opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                          {vendor.expenseCount != null
                            ? `${vendor.expenseCount} txn${vendor.expenseCount !== 1 ? "s" : ""}`
                            : "View"}
                        </span>
                        <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ── Pagination ───────────────────────────────────────────────────── */}
        {!isLoading && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border/60">
            <span className="text-xs text-muted-foreground tabular-nums">
              {((currentPage - 1) * pagination.limit) + 1}–{Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total.toLocaleString()}
            </span>
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="sm" onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1 || isLoading} className="h-7 gap-1 text-xs">
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </Button>
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let p: number;
                if (pagination.totalPages <= 5)           p = i + 1;
                else if (currentPage <= 3)                p = i + 1;
                else if (currentPage >= pagination.totalPages - 2) p = pagination.totalPages - 4 + i;
                else                                       p = currentPage - 2 + i;
                return (
                  <Button
                    key={p}
                    variant={p === currentPage ? "default" : "outline"}
                    size="sm"
                    onClick={() => goToPage(p)}
                    disabled={isLoading}
                    className="h-7 w-7 p-0 text-xs"
                  >
                    {p}
                  </Button>
                );
              })}
              <Button variant="outline" size="sm" onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= pagination.totalPages || isLoading} className="h-7 gap-1 text-xs">
                Next <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}

        {!isLoading && pagination.total > 0 && pagination.totalPages <= 1 && (
          <div className="px-4 py-3 border-t border-border/60">
            <span className="text-xs text-muted-foreground">
              {pagination.total} vendor{pagination.total !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Vendors;
